import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Stack } from "@mui/material";
import axios from "axios";
import "../sidebar/sidebar.scss";
import "react-toastify/dist/ReactToastify.css";
import ProfileModal from "../modals/profile modal/profileModal";
import { BaseUrl, ImageBaseUrl } from "../../Environment";

import customerloan from "../../assets/images/customerloan.svg";
import deposit from "../../assets/images/deposit.svg";
import dashboard from "../../assets/images/dashboard.svg";
import details from "../../assets/images/details.svg";
import upgrade from "../../assets/images/upgrade.svg";
import tickets from "../../assets/images/tickets.svg";
import help from "../../assets/images/help.svg";
import branch from "../../assets/images/branch.svg";
import loan from "../../assets/images/loan.svg";
import members from "../../assets/images/members.svg";
import expense from "../../assets/images/expense.svg";
import userdefault from "../../assets/images/userdefault.svg";
import reportsicon from "../../assets/images/reportsicon.svg";
import printDoc from "../../assets/images/printDoc.svg";
import settings from "../../assets/images/settings.svg";
import timeReport from "../../assets/images/time_report.svg";
import loanReport from "../../assets/images/loan_report.svg";
import chesscoin from "../../assets/images/chesscoin.svg";
import refer from "../../assets/images/refer.svg";
import disburseReportImg from "../../assets/images/disburse_report.svg";
import rejectedReportImg from "../../assets/images/rejected_report.svg";
import qrcode from "../../assets/images/qrcode.svg";
import detailedReportImg from "../../assets/images/detailed_report.svg";
import userReport from "../../assets/images/user_report.svg";
import ensureDefaultBankAccount, { getSecureItem } from "../../utilsFunctions";
import ReferralModal from "../modals/ReferralModal/ReferralModal";

// ─── Submenu key constants ────────────────────────────────────────────────────
const MENU = {
  CUSTOMER: "customer",
  LOAN: "loan",
  DEPOSIT: "deposit",
  INVESTOR: "investor",
  COLLATERALS: "collaterals",
  REPORTS: "reports",
  ACCOUNTING: "accounting",
};

// ─── Route → active key map ───────────────────────────────────────────────────
const ROUTE_MAP = {
  dashboard: ["/dashboard"],
  allnewcustomers: [
    "/allnewcustomers",
    "/customers/addcustomer",
    "/customers/csvImportExport",
  ],
  customer: [
    "/customer",
    "/addloannointerest",
    "/loandisbursement",
    "/addFeeDetails",
    "/loan/loanCsvImport",
  ],
  deposit: [
    "/schemes",
    "/addeditscheme",
    "/savings",
    "/addsaving"
  ],
  loantype: ["/loantype", "/loan/editloan", "/addloan"],
  investor: ["/viewInvestor", "/addInvestor"],
  collaterals: ["/addCollateral", "/viewCollateral"],
  collectionreport: ["/collectionreport"],
  expense: ["/expenses"],
  otherIncome: ["/otherincome"],
  customPayment: ["/custompayment"],
  printDocs: ["/printdocs"],
  profitLoss: ["/profitloss", "/cashflow", "/bankaccountmanagement"],
  reports: [
    "/collectionreport",
    "/customerwisereport",
    "/loanwisereport",
    "/daywisesavingsreport",
    "/savingswisereport",
    "/customerwisesavingsreport",
    "/collectorwisereport",
    "/loanbeyond",
    "/arrearreport",
    "/longpendingdues",
    "/disbursereport",
    "/rejectionreport",
    "/detailedanalysisreport",
    "/borroweraccountstatement",
  ],
  profile: [
    "/usermanagement",
    "/companyprofile",
    "/updateadmin",
    "/updatepassword",
  ],
  help: ["/help"],
  upgrade: ["/pricing"],
  tickets: ["/tickets"],
  settings: ["/settings"],
  branch: ["/branch"],
  partner: ["/partner"],
};

const getActiveStyles = (path) =>
  Object.fromEntries(
    Object.entries(ROUTE_MAP).map(([key, routes]) => [
      key,
      routes.some((r) => path == r || path.startsWith(r + "/")),
    ]),
  );

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = () => {
  const userPermissions = getSecureItem("UsersAccess");
  const loggedinUser = getSecureItem("loggedinUser");

  const [openModal, setOpenModal] = useState(false);
  const [modalReferral, setModalReferral] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // single open submenu
  const [activeStyle, setActiveStyle] = useState({});
  const [profileCard, setProfileCard] = useState("2px solid lightgrey");
  const [loggedinUserDetails, setLoggedinUserDetails] = useState(null);

  const location = useLocation();
  const history = useHistory();

  // Toggle one submenu; close if already open
  const toggleMenu = useCallback(
    (key) => setOpenMenu((prev) => (prev == key ? null : key)),
    [],
  );

  const getData = useCallback(async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getSecureItem("token")}`,
      };
      const { userId } = getSecureItem("loggedinUser");
      const { status, data } = await axios.get(
        `${BaseUrl}/user/getUserDetails?userId=${userId}`,
        { headers },
      );
      if (status == 200) setLoggedinUserDetails(data);
      else
        toast.error("Please try again later!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
        });
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    ensureDefaultBankAccount();
  }, []);

  useEffect(() => {
    if (!getSecureItem("token")) {
      history.push("/login");
      return;
    }
    getData();
    const styles = getActiveStyles(location.pathname);
    setActiveStyle(styles);
    setProfileCard(
      styles.profile ? "2px solid #00D95E" : "2px solid lightgrey",
    );
  }, [location, history, getData]);

  const isSuperAdmin = loggedinUser?.userType == "superAdmin";
  const isAdminOrSuper = ["superAdmin", "Admin"].includes(
    loggedinUser?.userType,
  );

  return (
    <div className="sidepage">
      <div className="area" />
      <nav className="main-menu">
        <ul>
          {/* Dashboard */}
          <MenuItem
            isActive={activeStyle.dashboard}
            path="/dashboard"
            icon={dashboard}
            tooltip="Dashboard"
          />

          {/* Borrowers */}
          {userPermissions?.viewBorrowers && (
            <MenuItem
              isActive={activeStyle.allnewcustomers}
              onClick={() => toggleMenu(MENU.CUSTOMER)}
              icon={members}
              tooltip="Borrowers"
            />
          )}
          {openMenu == MENU.CUSTOMER && (
            <SubMenuGroup>
              {userPermissions?.addBorrowers && (
                <MenuItem
                  path="/customers/addcustomer"
                  icon={members}
                  tooltip="Add New Borrower"
                  fontSize={10}
                />
              )}
              {userPermissions?.addBorrowers && (
                <MenuItem
                  path="/customers/csvImportExport"
                  icon={members}
                  tooltip="Import Borrowers"
                  fontSize={10}
                />
              )}
              {userPermissions?.viewBorrowers && (
                <MenuItem
                  path="/allnewcustomers"
                  icon={members}
                  tooltip="View All Borrowers"
                  fontSize={10}
                />
              )}
            </SubMenuGroup>
          )}

          {/* Loans */}
          {(userPermissions?.viewLoan || userPermissions?.approveLoan) && (
            <MenuItem
              isActive={activeStyle.customer}
              onClick={() => toggleMenu(MENU.LOAN)}
              icon={customerloan}
              tooltip="Loans"
            />
          )}
          {openMenu == MENU.LOAN && (
            <SubMenuGroup>
              {userPermissions?.addLoan && (
                <MenuItem
                  path="/addloannointerest"
                  icon={customerloan}
                  tooltip="Add New Loan"
                  fontSize={10}
                />
              )}
              {userPermissions?.addLoan && (
                <MenuItem
                  path="/loan/loanCsvImport"
                  icon={customerloan}
                  tooltip="Import Loans"
                  fontSize={10}
                />
              )}
              {userPermissions?.approveLoan && (
                <MenuItem
                  path="/loandisbursement"
                  icon={members}
                  tooltip="Approve & Disburse Loans"
                  fontSize={10}
                />
              )}
              {userPermissions?.viewLoan && (
                <MenuItem
                  path="/customer"
                  icon={customerloan}
                  tooltip="View All Loans"
                  fontSize={10}
                />
              )}
              {userPermissions?.viewLoan && (
                <MenuItem
                  path="/addFeeDetails"
                  icon={customerloan}
                  tooltip="Add Fee Details"
                  fontSize={10}
                />
              )}
            </SubMenuGroup>
          )}

          {/* Deposits */}
          {userPermissions?.viewLoan && (
            <MenuItem
              isActive={activeStyle.deposit}
              onClick={() => toggleMenu(MENU.DEPOSIT)}
              icon={deposit}
              tooltip="Savings & Deposits"
            />
          )}
          {openMenu == MENU.DEPOSIT && (
            <SubMenuGroup>
              {userPermissions?.viewLoan && (
                <MenuItem
                  path="/schemes"
                  icon={deposit}
                  tooltip="Savings/Schemes Type"
                  fontSize={10}
                />
              )}
              {userPermissions?.viewLoan && (
                <MenuItem
                  path="/savings"
                  icon={deposit}
                  tooltip="View All Savings"
                  fontSize={10}
                />
              )}
              <MenuItem
                path="/daywisesavingsreport"
                icon={loanReport}
                tooltip="Day-Wise Savings Collection Report"
                fontSize={10}
              />
              <MenuItem
                path="/savingswisereport"
                icon={loanReport}
                tooltip="Savings-Wise Report"
                fontSize={10}
              />
              <MenuItem
                path="/customerwisesavingsreport"
                icon={loanReport}
                tooltip="Customer-Wise Savings Report"
                fontSize={10}
              />
            </SubMenuGroup>
          )}

          {/* Loan Type */}
          <MenuItem
            isActive={activeStyle.loantype}
            path="/loantype"
            icon={loan}
            tooltip="Loan Type"
          />

          {/* Investors */}
          <MenuItem
            isActive={activeStyle.investor}
            onClick={() => toggleMenu(MENU.INVESTOR)}
            icon={chesscoin}
            tooltip="Investor"
          />
          {openMenu == MENU.INVESTOR && (
            <SubMenuGroup>
              <MenuItem
                path="/addInvestor"
                icon={chesscoin}
                tooltip="Add New Investor"
                fontSize={10}
              />
              <MenuItem
                path="/viewInvestor"
                icon={chesscoin}
                tooltip="View All Investors"
                fontSize={10}
              />
            </SubMenuGroup>
          )}

          {/* Collaterals */}
          <MenuItem
            isActive={activeStyle.collaterals}
            onClick={() => toggleMenu(MENU.COLLATERALS)}
            icon={details}
            tooltip="Collaterals"
          />
          {openMenu == MENU.COLLATERALS && (
            <SubMenuGroup>
              <MenuItem
                path="/addCollateral"
                icon={details}
                tooltip="Add Collateral"
                fontSize={10}
              />
              <MenuItem
                path="/viewCollateral"
                icon={details}
                tooltip="View Collaterals"
                fontSize={10}
              />
            </SubMenuGroup>
          )}

          {/* Reports */}
          {userPermissions?.viewReports && (
            <MenuItem
              isActive={activeStyle.reports}
              onClick={() => toggleMenu(MENU.REPORTS)}
              icon={reportsicon}
              tooltip="Reports"
            />
          )}
          {openMenu == MENU.REPORTS && (
            <SubMenuGroup>
              <MenuItem
                path="/collectionreport"
                icon={timeReport}
                tooltip="Day-Wise Collection Report"
                fontSize={10}
              />
              <MenuItem
                path="/arrearreport"
                icon={loanReport}
                tooltip="Day-Wise Demand Report"
                fontSize={10}
              />
              <MenuItem
                path="/outstandingduesreport"
                icon={loanReport}
                tooltip="Outstanding Dues Report"
                fontSize={10}
              />
              <MenuItem
                path="/customerwisereport"
                icon={userReport}
                tooltip="Customer-Wise Collection Report"
                fontSize={10}
              />
              <MenuItem
                path="/loanwisereport"
                icon={loanReport}
                tooltip="Loan-Wise Collection Report"
                fontSize={10}
              />
              <MenuItem
                path="/collectorwisereport"
                icon={userReport}
                tooltip="Agent-Wise Collection Report"
                fontSize={10}
              />
              <MenuItem
                path="/borroweraccountstatement"
                icon={userReport}
                tooltip="Borrower Account Statement"
                fontSize={10}
              />
              <MenuItem
                path="/loanbeyond"
                icon={loanReport}
                tooltip="Loan Beyond Total Tenure"
                fontSize={10}
              />
              <MenuItem
                path="/longpendingdues"
                icon={loanReport}
                tooltip="Long Pending Dues"
                fontSize={10}
              />
              <MenuItem
                path="/disbursereport"
                icon={disburseReportImg}
                tooltip="Disbursement Report"
                fontSize={10}
              />
              <MenuItem
                path="/rejectionreport"
                icon={rejectedReportImg}
                tooltip="Rejection Report"
                fontSize={10}
              />
              <MenuItem
                path="/detailedanalysisreport"
                icon={detailedReportImg}
                tooltip="Detailed Analysis Report"
                fontSize={10}
              />
            </SubMenuGroup>
          )}

          {/* Expenses */}
          {userPermissions?.Expenses && (
            <MenuItem
              isActive={activeStyle.expense}
              path="/expenses"
              icon={expense}
              tooltip="Expenses"
            />
          )}

          {/* Other Income */}
          {userPermissions?.accounting && (
            <MenuItem
              isActive={activeStyle.otherIncome}
              path="/otherincome"
              icon={details}
              tooltip="Other Income"
            />
          )}

          {/* Accounting */}
          {(
            userPermissions?.accounting ||
            userPermissions?.cashFlow ||
            userPermissions?.profitLoss
          ) && (
            <MenuItem
              isActive={activeStyle.profitLoss}
              onClick={() => toggleMenu(MENU.ACCOUNTING)}
              icon={expense}
              tooltip="Accounting"
            />
          )}

          {openMenu === MENU.ACCOUNTING && (
            <SubMenuGroup>
              {userPermissions?.profitLoss && (
                <MenuItem
                  path="/profitloss"
                  icon={details}
                  tooltip="Profit & Loss Statement"
                  fontSize={10}
                />
              )}
              {userPermissions?.cashFlow && (
                <MenuItem
                  path="/cashflow"
                  icon={details}
                  tooltip="Cash Flow"
                  fontSize={10}
                />
              )}
              {userPermissions?.accounting && (
                <MenuItem
                  path="/bankaccountmanagement"
                  icon={details}
                  tooltip="Bank Accounts"
                  fontSize={10}
                />
              )}
            </SubMenuGroup>
          )}

          {/* Manage Subscriptions (internal) */}
          {loggedinUser?.companyId == 2414 && isSuperAdmin && (
            <MenuItem
              isActive={activeStyle.customPayment}
              path="/custompayment"
              icon={details}
              tooltip="Manage Subscriptions"
            />
          )}

          {/* Print Docs */}
          {userPermissions?.printDocs && (
            <MenuItem
              isActive={activeStyle.printDocs}
              path="/printdocs"
              icon={printDoc}
              tooltip="Generate Loan Documents"
            />
          )}
        </ul>

        <ul className="logout">
          {/* Profile */}
          <MenuItem
            isActive={activeStyle.profile}
            onClick={() => setOpenModal(true)}
            icon={
              loggedinUserDetails?.userImagePath
                ? `${ImageBaseUrl}${loggedinUserDetails.userImagePath}`
                : userdefault
            }
            tooltip="Profile"
          />

          {/* Digital Payments */}
          {getSecureItem("country")?.code == 91 && isSuperAdmin && (
            <MenuItem
              isActive={activeStyle.partner}
              path="/partner"
              icon={qrcode}
              tooltip="Digital Payments"
            />
          )}

          {/* Branches */}
          {isSuperAdmin && loggedinUser?.company?.planId == 3 && (
            <MenuItem
              isActive={activeStyle.branch}
              path="/branch"
              icon={branch}
              tooltip="Branches"
            />
          )}

          {/* Settings */}
          {isSuperAdmin && (
            <MenuItem
              isActive={activeStyle.settings}
              path="/settings"
              icon={settings}
              tooltip="Settings"
            />
          )}

          {/* Upgrade */}
          {isSuperAdmin && (
            <MenuItem
              isActive={activeStyle.upgrade}
              path="/pricing"
              icon={upgrade}
              tooltip="Upgrade"
            />
          )}

          {isSuperAdmin && (
            <MenuItem
              isActive={activeStyle.tickets}
              path="/tickets"
              icon={tickets}
              tooltip="Support"
            />
          )}

          {/* Refer & Earn */}
          <MenuItem
            onClick={() => setModalReferral(true)}
            icon={refer}
            tooltip="Refer & Earn"
          />

          {/* Help */}
          <MenuItem
            isActive={activeStyle.help}
            path="/help"
            icon={help}
            tooltip="Help"
          />
        </ul>

        <ProfileModal open={openModal} close={() => setOpenModal(false)} />
        {modalReferral && (
          <ReferralModal
            open={modalReferral}
            close={() => setModalReferral(false)}
          />
        )}
      </nav>
    </div>
  );
};

// ─── SubMenuGroup ─────────────────────────────────────────────────────────────
const SubMenuGroup = ({ children }) => (
  <div style={{ backgroundColor: "gray" }}>{children}</div>
);

// ─── MenuItem ─────────────────────────────────────────────────────────────────
const MenuItem = ({
  isActive = false,
  path,
  icon,
  tooltip,
  onClick,
  fontSize,
}) => (
  <div
    className="borderLeft"
    style={{ borderLeft: isActive ? "3px solid #00D95E" : "" }}
  >
    <li
      className="liClass Fonts"
      style={{ backgroundColor: isActive ? "#1fab98" : "" }}
      onClick={onClick}
    >
      {path ? (
        <Link to={path}>
          <MenuItemContent icon={icon} tooltip={tooltip} fontSize={fontSize} />
        </Link>
      ) : (
        <MenuItemContent icon={icon} tooltip={tooltip} fontSize={fontSize} />
      )}
    </li>
  </div>
);

// ─── MenuItemContent ──────────────────────────────────────────────────────────
const MenuItemContent = ({ icon, tooltip, fontSize }) => (
  <Stack spacing={fontSize ? 1.5 : 3} direction="row" alignItems="center">
    <img
      className="fa fa-2x"
      style={{
        width: fontSize ? "15px" : undefined,
        height: fontSize ? "15px" : undefined,
      }}
      src={icon}
      alt={""}
    />
    <h6 style={{ color: "white", fontSize: fontSize ?? 13 }}>{tooltip}</h6>
  </Stack>
);

export default Sidebar;
